import React, { FC, useEffect, useState } from 'react'
import Button from '@mortgage-pos/ui/components/Button'
import { useStoreState, useStoreActions } from '@mortgage-pos/ui/store'
import { RateSelectPageVariation } from '@mortgage-pos/types'
import { iconMap } from '@mortgage-pos/ui/utils/customizationHelpers'
import Loading from '@mortgage-pos/ui/components/Loading'
import RateCardCompare from '../RateCardCompare'
import { filterAndPositionOffers } from '@mortgage-pos/ui/utils/BankrateCompare'
import { formatNumberToDollarAmount } from '@mortgage-pos/utils'
import incense from '@mortgage-pos/ui/incense'

interface RatesVariationDetails extends Partial<RateSelectPageVariation> {
  icon?: string
  description1?: string
  description2?: string
}

interface RatesVariationPropsContent {
  noRates: RatesVariationDetails
  greatRates: RatesVariationDetails
  cashOut: RatesVariationDetails
}

interface RatesVariationProps {
  variation: string
  cashOutAmount?: number
  submitRate?: (rate) => void
}

const ratesVariationContent: RatesVariationPropsContent = {
  noRates: {
    title: 'No Results Found',
    description1: "We're sorry we couldn't find what you were looking for.",
    description2: 'Please go back and try again or visit our rates page.',
    icon: iconMap('brComputerWithX'),
    cta: 'Visit our rates page',
  },
  greatRates: {
    title: 'You already have a great rate, congrats',
    description1: 'Kudos to you and your hard work saving tons of money.',
    icon: iconMap('brOnlineApp'),
    cta: 'Visit our rates page',
  },
  cashOut: {
    title: 'A cash-out refinance might be right for you',
    description1:
      'Access your home equity to pay for anything you want like home remodeling, paying of credit card debt or other investing opportunities.',
  },
}

const RatesVariation: FC<RatesVariationProps> = ({
  variation,
  cashOutAmount,
  submitRate,
}) => {
  const { answers } = useStoreState((state) => state.answers)
  const [hasRequestedRates, setHasRequestedRates] = useState<boolean>(false)
  const [ratesVariation, setRatesVariation] = useState<string>(variation)
  const [cashOutRates, setCashOutRates] = useState([])

  const { rawLEData } = useStoreState((state) => state.leComparison)
  const { elementClicked, elementViewed } = useStoreActions(
    (store) => store.tracking
  )

  const { requestBankrateRates } = useStoreActions(
    (store) => store.bankrateCompare
  )

  const { title, icon, description1, description2, cta } =
    ratesVariationContent[ratesVariation]

  useEffect(() => {
    const tagularEventPayload = {
      elementType: `${ratesVariation} page`,
      text: ratesVariationContent[ratesVariation]?.title,
    }

    elementViewed(tagularEventPayload)
  }, [])

  useEffect(() => {
    if (ratesVariation !== 'cashOut') {
      setHasRequestedRates(true)
      return
    }

    async function getRates() {
      try {
        // @ts-ignore
        const ratesResponse = await requestBankrateRates(cashOutAmount)

        if (!ratesResponse.length) {
          setRatesVariation('greatRates')
          setHasRequestedRates(true)
          return
        }

        // Initializing an instance without refiReason as the first argument to avoid filtering rates
        const filteredOffers = new filterAndPositionOffers('', ratesResponse)

        setCashOutRates(filteredOffers.getCashOutOffers())
      } catch (err) {
        incense(err)
          .details({
            name: 'failureRetrievingCashoutRates',
            message: 'Failed to filter and retrieve cashout rates',
          })
          .error()

        setRatesVariation('greatRates')
        setHasRequestedRates(true)
      }

      setHasRequestedRates(true)
    }
    getRates()
  }, [])

  const rateTableLink = () => {
    elementClicked({
      elementType: 'Button',
      location: `${variation} page`,
      actionOutcome: `Routes user to Bankrate rates table`,
      text: cta,
    })

    const bankrateBaseUrl =
      'https://www.bankrate.com/mortgages/mortgage-rates/?partnerId=br3&pid=br3&pointsChanged=false&searchChanged=false&disablePre=1&'
    const bankrateUrlParams = new URLSearchParams()
    bankrateUrlParams.append('refinanceCashOutAmount', answers.cashOut || 0)
    bankrateUrlParams.append('refinanceLoanAmount', answers.remainingBalance)
    bankrateUrlParams.append(
      'refinanceLoanTerms',
      (rawLEData?.term || answers.currentLoanTerm) + 'yr'
    )
    bankrateUrlParams.append('refinancePropertyType', answers.propertyType)
    bankrateUrlParams.append('PrimaryResidence', answers.propertyUse)
    bankrateUrlParams.append('refinancePropertyValue', answers.estimatedValue)
    bankrateUrlParams.append('zipCode', answers.zipCode)

    window.location.href = bankrateBaseUrl + bankrateUrlParams.toString()
  }

  if (!hasRequestedRates) {
    return <Loading loaderType="house" shouldWrap={false} />
  }

  return (
    <div className="br-rates-variation__container">
      {ratesVariation === 'cashOut' ? (
        <h2 className="br-rates-variation__cashout-heading">
          YOU HAVE A GREAT RATE!
        </h2>
      ) : (
        <img
          className="br-rates-variation__icon"
          src={icon}
          alt="No Rates Icon"
        />
      )}
      <h1 className="compare-page__title br-rates-variation__title">{title}</h1>
      <div className="br-rates-variation__description-container">
        <p>
          {description1} <span>{description2}</span>
        </p>
      </div>

      {ratesVariation === 'cashOut' ? (
        <>
          <div className="br-rates-variation__cashout-amount">
            We calculated you can take up to
            <span> {formatNumberToDollarAmount(cashOutAmount, 0)} </span>
            cash-out!
          </div>
          <div className={`rate-selections__options-list --row --bankrate`}>
            {cashOutRates?.map((rate, rateIndex) => (
              <RateCardCompare
                rate={rate}
                superlativeName={rate}
                handleSubmitRateClick={() => submitRate(rate)}
                cta={'Next'}
                bestRate={rateIndex === 0}
                key={'cashoutRates ' + rateIndex}
              />
            ))}
          </div>
        </>
      ) : (
        <div className="br-rates-variation__button-wrapper">
          <Button
            className="button button--dark rate-card-compare__lock-button br-compare__lock-button"
            content={cta}
            onClick={rateTableLink}
          />
        </div>
      )}
    </div>
  )
}

export default RatesVariation
